import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUserId, getIsEsignTaskViewed, getIsLoanDetailsUpdateViewed, getLoanId, getNhDealApplicationId } from "features/authSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { useGetNotificationDetailsQuery, NotificationResponse, useSetStatusFlagOfNotificationMutation, NotificationType } from "pages/authentication/userService";
import { PalNotificationProps } from "components/v2/notifications";
import { NeedType, PendingItem, useGetLoanSummaryDetailQuery, useGetNeedsQuery } from "pages/dashboard/home/dashboardService";
import { ESIGN_STATUS_NEW, NOTIFICATIONS_ESIGN_TITLE, NOTIFICATIONS_ESIGN_TEXT } from "utilities/Constant";

interface UseNotificationsOptions {
  enabled?: boolean;
}

export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const { enabled = true } = options;
  const dispatch = useDispatch();
  const [notifications, setNotifications] = useState<PalNotificationProps[]>([]);
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanId = useSelector(getLoanId);
  const isEsignTaskViewed = useSelector(getIsEsignTaskViewed);
  const isLoanDetailsUpdateViewed = useSelector(getIsLoanDetailsUpdateViewed);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const {
    data: pendingItemsV2,
    isLoading: isNeedsListLoading,
    error: pendingItemsV2Error,
  } = useGetNeedsQuery(
    { nhDealApplicationId, userId },
    {
      skip: false,
      refetchOnMountOrArgChange: false,
    }
  );
  const {
    data: loanSummary,
    isLoading: isLoanSummaryLoading,
    isError: isLoanSummaryError
  } = useGetLoanSummaryDetailQuery(
    { nhDealApplicationId, loanId, persistState: true },
    { skip: !nhDealApplicationId || !loanId || !featureFlag?.loanDetailsEnabled }
  );


  const {
    data: notificationData,
    isLoading,
    error,
  } = useGetNotificationDetailsQuery(userId, {
    skip: !enabled || !featureFlag?.preApprovalLetterEnabled,
  });

  const [updateNotificationStatus] = useSetStatusFlagOfNotificationMutation();
  const extractPalInstanceId = (link: string): string | undefined => {
    if (link && link.length > 0 && !link.includes("/")) {
      return link;
    }
    if (link?.includes("/")) {
      try {
        const url = new URL(link, globalThis.location.origin);
        return url.searchParams.get("palInstanceId") || undefined;
      } catch {
        return undefined;
      }
    }

    return undefined;
  };

  useEffect(() => {
    if (!notificationData || !nhDealApplicationId) return;
  
    setNotifications(prev => {
      const map = new Map(prev.map(n => [n.id, n]));
  
      for (const n of notificationData) {
        if (
          n.nhDealApplicationId != null &&
          n.nhDealApplicationId !== nhDealApplicationId
        ) {
          continue;
        }
  
        map.set(n.id, {
          id: n.id,
          userId: n.userId,
          nhDealApplicationId: n.nhDealApplicationId,
          icon:
            n.notificationType?.toLowerCase() === "esign"
              ? "paper"
              : "folder",
          title: n.title,
          text: n.text,
          createdOn: new Date(n.createdOn),
          notificationType: n.notificationType,
          palInstanceId: extractPalInstanceId(n.link),
        });
      }
  
      return Array.from(map.values());
    });
  }, [notificationData, nhDealApplicationId]);

  useEffect(() => {
    if (isNeedsListLoading || pendingItemsV2Error || !pendingItemsV2) return;

    // Avoid adding duplicate eSign notifications
    if (notifications.filter(n => n.notificationType === NotificationType.Esign).length > 0) return;

    pendingItemsV2.forEach((item: PendingItem) => {
      if (item.needType === NeedType.ESign && item.status?.toLowerCase() === ESIGN_STATUS_NEW && !isEsignTaskViewed) {
        const eSignDate = new Date(item.documents?.[0]?.uploadedDateTime || Date.now());
        setNotifications([
          ...notifications,
          {
            id: item.id,
            title: NOTIFICATIONS_ESIGN_TITLE,
            text: NOTIFICATIONS_ESIGN_TEXT,
            icon: "paper",
            createdOn: eSignDate,
            userId,
            nhDealApplicationId: nhDealApplicationId,
            notificationType: NotificationType.Esign
          }
        ]);
      }
    });
  }, [pendingItemsV2]);

  useEffect(() => {
    if (isLoanSummaryLoading || isLoanSummaryError || !loanSummary || isLoanDetailsUpdateViewed) return;

    // Check for updated loan fields and create a notification if appropriate
    if (loanSummary.updatedFields && loanSummary.updatedFields.length > 0) {
      setNotifications([
        ...notifications,
        {
          id: `loan-update-${Date.now()}`,
          title: "Loan Details Updated",
          text: "Your loan details have been updated",
          icon: "paper",
          createdOn: new Date(),
          userId,
          nhDealApplicationId: nhDealApplicationId,
          notificationType: NotificationType.LoanDetails
        }
      ])
    }
  }, [loanSummary]);

  const removeNotification = async (notificationId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    await updateNotificationStatus(notificationId).catch(() => { });
  };

  const clearAllNotifications = async () => {
    if (notifications.length === 0) return;
    const notificationIds = notifications.map((n) => n.id);
    setNotifications([]);
    await Promise.all(notificationIds.map((id) => updateNotificationStatus(id).catch(() => { })));
  };

  return {
    notifications,
    isLoading,
    error,
    removeNotification,
    clearAllNotifications,
    hasNotifications: notifications.length > 0,
    notificationCount: notifications.length,
  };
};
